import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Box, Camera, RotateCcw, Sun, ExternalLink,
  Mountain, Play, Pause, Compass, Eye, MapPin, Image
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const View3DPlanner = ({ latitude, longitude, sunTimes }) => {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const [viewAngle, setViewAngle] = useState(0);
  const [elevation, setElevation] = useState(90);
  const [focalLength, setFocalLength] = useState(24);
  const [timeOfDay, setTimeOfDay] = useState(12);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);

  // Calculate sun position for given hour
  const calculateSunPosition = useCallback((hour) => {
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
    
    const solarNoon = 12.0 - longitude / 15.0;
    const hourAngle = 15 * (hour - solarNoon);
    
    const latRad = latitude * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    const haRad = hourAngle * Math.PI / 180;
    
    const sinAlt = Math.sin(latRad) * Math.sin(decRad) + 
                   Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
    const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;
    
    const cosAz = (Math.sin(decRad) - Math.sin(latRad) * Math.sin(altitude * Math.PI / 180)) / 
                  (Math.cos(latRad) * Math.cos(altitude * Math.PI / 180) + 0.0001);
    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
    if (hourAngle > 0) azimuth = 360 - azimuth;
    
    return { azimuth, altitude };
  }, [latitude, longitude]);

  const currentSunPos = useMemo(() => calculateSunPosition(timeOfDay), [timeOfDay, calculateSunPosition]);

  // Open Google Street View
  const openGoogleStreetView = () => {
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}&heading=${viewAngle}&pitch=${elevation - 90}&fov=${Math.round(2 * Math.atan(36 / (2 * focalLength)) * 180 / Math.PI)}`;
    window.open(url, '_blank');
  };

  // Open Mapillary
  const openMapillary = () => {
    const url = `https://www.mapillary.com/app/?lat=${latitude}&lng=${longitude}&z=17`;
    window.open(url, '_blank');
  };

  // Open Google Earth
  const openGoogleEarth = () => {
    const url = `https://earth.google.com/web/@${latitude},${longitude},100a,500d,35y,${viewAngle}h,${90 - elevation}t,0r`;
    window.open(url, '_blank');
  };

  // Canvas drawing for simulation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Sky gradient based on time of day
    const sunAlt = currentSunPos.altitude;
    let skyTopColor, skyBottomColor, horizonColor;
    
    if (sunAlt > 6) {
      // Day
      skyTopColor = '#1e90ff';
      skyBottomColor = '#87ceeb';
      horizonColor = '#b0e0e6';
    } else if (sunAlt > 0) {
      // Golden hour
      skyTopColor = '#4a6fa5';
      skyBottomColor = '#ff8c42';
      horizonColor = '#ffd700';
    } else if (sunAlt > -6) {
      // Blue hour
      skyTopColor = '#1a1a4e';
      skyBottomColor = '#4a5568';
      horizonColor = '#ff6b6b';
    } else if (sunAlt > -12) {
      // Twilight
      skyTopColor = '#0a0a2e';
      skyBottomColor = '#1a1a3e';
      horizonColor = '#2d3748';
    } else {
      // Night
      skyTopColor = '#000011';
      skyBottomColor = '#0a0a1a';
      horizonColor = '#1a1a2e';
    }
    
    // Draw sky gradient
    const skyGradient = ctx.createLinearGradient(0, 0, 0, height * 0.6);
    skyGradient.addColorStop(0, skyTopColor);
    skyGradient.addColorStop(0.7, skyBottomColor);
    skyGradient.addColorStop(1, horizonColor);
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, width, height);

    // Draw stars at night
    if (sunAlt < -6) {
      ctx.fillStyle = 'white';
      for (let i = 0; i < 150; i++) {
        const x = (Math.sin(i * 567.89 + viewAngle * 0.1) * 0.5 + 0.5) * width;
        const y = (Math.cos(i * 123.45) * 0.5 + 0.5) * height * 0.5;
        const size = Math.random() * 1.5 + 0.5;
        const brightness = Math.random() * 0.5 + 0.5;
        ctx.globalAlpha = brightness;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    // Draw sun or moon
    const sunRelativeAz = currentSunPos.azimuth - viewAngle;
    let sunScreenX = width / 2 + (sunRelativeAz) * (width / 90);
    if (sunScreenX < -50) sunScreenX += width * 4;
    if (sunScreenX > width + 50) sunScreenX -= width * 4;
    
    const lookUpDown = elevation - 90;
    const sunScreenY = height * 0.5 - (currentSunPos.altitude - lookUpDown) * (height / 60);
    
    if (sunAlt > -6 && sunScreenX > -50 && sunScreenX < width + 50) {
      // Sun
      const sunSize = sunAlt > 6 ? 30 : 40;
      const sunGradient = ctx.createRadialGradient(sunScreenX, sunScreenY, 0, sunScreenX, sunScreenY, sunSize * 2);
      
      if (sunAlt > 6) {
        sunGradient.addColorStop(0, 'rgba(255, 255, 220, 1)');
        sunGradient.addColorStop(0.3, 'rgba(255, 240, 150, 0.8)');
        sunGradient.addColorStop(0.6, 'rgba(255, 200, 100, 0.3)');
        sunGradient.addColorStop(1, 'rgba(255, 150, 50, 0)');
      } else {
        sunGradient.addColorStop(0, 'rgba(255, 100, 50, 1)');
        sunGradient.addColorStop(0.3, 'rgba(255, 80, 30, 0.8)');
        sunGradient.addColorStop(0.6, 'rgba(255, 50, 20, 0.4)');
        sunGradient.addColorStop(1, 'rgba(200, 30, 10, 0)');
      }
      
      ctx.fillStyle = sunGradient;
      ctx.beginPath();
      ctx.arc(sunScreenX, sunScreenY, sunSize * 2, 0, Math.PI * 2);
      ctx.fill();
      
      // Sun core
      ctx.fillStyle = sunAlt > 6 ? '#ffffd0' : '#ff6030';
      ctx.beginPath();
      ctx.arc(sunScreenX, sunScreenY, sunSize * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw clouds
    if (sunAlt > -6) {
      ctx.fillStyle = sunAlt > 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(100, 100, 120, 0.5)';
      for (let i = 0; i < 5; i++) {
        const cloudX = ((i * 234.5 + viewAngle * 2) % (width + 200)) - 100;
        const cloudY = 50 + (i * 37) % 100;
        
        for (let j = 0; j < 4; j++) {
          ctx.beginPath();
          ctx.arc(cloudX + j * 25, cloudY + Math.sin(j) * 10, 20 + j * 5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Draw terrain/mountains
    const horizonY = height * 0.55 + lookUpDown * 2;
    
    // Far mountains
    ctx.fillStyle = sunAlt > 0 ? '#5a6a7a' : '#2a3a4a';
    ctx.beginPath();
    ctx.moveTo(0, horizonY);
    for (let x = 0; x <= width; x += 5) {
      const mountainHeight = 
        Math.sin((x + viewAngle * 10) * 0.008) * 60 +
        Math.sin((x + viewAngle * 10) * 0.02) * 30 +
        Math.sin((x + viewAngle * 10) * 0.05) * 15;
      ctx.lineTo(x, horizonY - mountainHeight - 40);
    }
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.fill();

    // Near hills
    ctx.fillStyle = sunAlt > 0 ? '#3a4a3a' : '#1a2a1a';
    ctx.beginPath();
    ctx.moveTo(0, horizonY + 20);
    for (let x = 0; x <= width; x += 5) {
      const hillHeight = 
        Math.sin((x + viewAngle * 15) * 0.015) * 30 +
        Math.sin((x + viewAngle * 15) * 0.04) * 15;
      ctx.lineTo(x, horizonY - hillHeight + 20);
    }
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.fill();

    // Foreground
    const groundGradient = ctx.createLinearGradient(0, horizonY + 40, 0, height);
    groundGradient.addColorStop(0, sunAlt > 0 ? '#2a3a2a' : '#0a1a0a');
    groundGradient.addColorStop(1, sunAlt > 0 ? '#1a2a1a' : '#050a05');
    ctx.fillStyle = groundGradient;
    ctx.fillRect(0, horizonY + 40, width, height - horizonY - 40);

    // Draw compass
    const compassX = width - 45;
    const compassY = 40;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(compassX, compassY, 25, 0, Math.PI * 2);
    ctx.stroke();
    
    const needleAngle = (-viewAngle) * Math.PI / 180;
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.moveTo(compassX + Math.sin(needleAngle) * 18, compassY - Math.cos(needleAngle) * 18);
    ctx.lineTo(compassX + Math.sin(needleAngle + 2.8) * 6, compassY - Math.cos(needleAngle + 2.8) * 6);
    ctx.lineTo(compassX + Math.sin(needleAngle - 2.8) * 6, compassY - Math.cos(needleAngle - 2.8) * 6);
    ctx.closePath();
    ctx.fill();
    
    ctx.fillStyle = 'white';
    ctx.fillRect(compassX - 1, compassY + 4, 2, 12);
    
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('N', compassX, compassY - 28);

    // Focal length frame
    const fov = 2 * Math.atan(36 / (2 * focalLength)) * 180 / Math.PI;
    const frameScale = 45 / fov;
    const frameW = width * Math.min(frameScale, 1);
    const frameH = height * Math.min(frameScale, 1);
    const frameX = (width - frameW) / 2;
    const frameY = (height - frameH) / 2;
    
    if (frameScale < 1) {
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(frameX, frameY, frameW, frameH);
      ctx.setLineDash([]);
    }

    // Info overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(8, height - 55, 170, 47);
    ctx.fillStyle = '#f59e0b';
    ctx.font = '11px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`☀ Az: ${currentSunPos.azimuth.toFixed(0)}° Alt: ${currentSunPos.altitude.toFixed(0)}°`, 15, height - 38);
    ctx.fillStyle = 'white';
    ctx.fillText(`👁 View: ${viewAngle}° | ${focalLength}mm`, 15, height - 20);

  }, [viewAngle, elevation, focalLength, timeOfDay, currentSunPos]);

  // Animation loop
  useEffect(() => {
    if (isAnimating) {
      animationRef.current = setInterval(() => {
        setTimeOfDay(prev => (prev + 0.1) % 24);
      }, 100);
    } else {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    }
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [isAnimating]);

  const getLightCondition = () => {
    const alt = currentSunPos.altitude;
    if (alt > 6) return { text: t('view3d.daylight'), color: 'text-amber-500', bg: 'bg-amber-500/20' };
    if (alt > 0) return { text: t('sun.goldenHour'), color: 'text-orange-500', bg: 'bg-orange-500/20' };
    if (alt > -6) return { text: t('sun.blueHour'), color: 'text-blue-500', bg: 'bg-blue-500/20' };
    if (alt > -12) return { text: t('view3d.twilight'), color: 'text-indigo-500', bg: 'bg-indigo-500/20' };
    return { text: t('view3d.night'), color: 'text-slate-400', bg: 'bg-slate-500/20' };
  };

  const lightCondition = getLightCondition();

  return (
    <div className="space-y-4">
      {/* Real Street View Links */}
      <Card className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border-emerald-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Image className="w-4 h-4 text-emerald-400" />
            {t('view3d.realStreetView')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400 mb-3">
            {t('view3d.seeRealPhotos')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openGoogleStreetView}
              className="border-emerald-500/30 hover:bg-emerald-500/20"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Google Street View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openMapillary}
              className="border-cyan-500/30 hover:bg-cyan-500/20"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Mapillary
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openGoogleEarth}
              className="border-blue-500/30 hover:bg-blue-500/20"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Google Earth 3D
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            📍 {latitude.toFixed(4)}°, {longitude.toFixed(4)}°
          </p>
        </CardContent>
      </Card>

      {/* Sun Position Simulator */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Sun className="w-4 h-4 text-amber-400" />
              {t('view3d.sunPositionSimulator')}
            </CardTitle>
            <Badge className={`${lightCondition.bg} ${lightCondition.color}`}>
              {lightCondition.text}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Canvas View */}
          <div className="relative rounded-lg overflow-hidden border border-white/10" style={{ height: '220px' }}>
            <canvas
              ref={canvasRef}
              width={400}
              height={220}
              className="w-full h-full"
            />
          </div>

          {/* Controls */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Compass className="w-3 h-3" />
                {t('view3d.direction')}: {viewAngle}°
              </Label>
              <Slider
                value={[viewAngle]}
                onValueChange={([v]) => setViewAngle(v)}
                min={0}
                max={360}
                step={5}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {t('view3d.look')}: {elevation - 90 > 0 ? '↓' : '↑'} {Math.abs(elevation - 90)}°
              </Label>
              <Slider
                value={[elevation]}
                onValueChange={([v]) => setElevation(v)}
                min={45}
                max={135}
                step={5}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Camera className="w-3 h-3" />
                {t('view3d.focal')}: {focalLength}mm
              </Label>
              <Slider
                value={[focalLength]}
                onValueChange={([v]) => setFocalLength(v)}
                min={14}
                max={200}
                step={1}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Sun className="w-3 h-3" />
                {t('view3d.time')}: {Math.floor(timeOfDay)}:{((timeOfDay % 1) * 60).toFixed(0).padStart(2, '0')}
              </Label>
              <Slider
                value={[timeOfDay]}
                onValueChange={([v]) => setTimeOfDay(v)}
                min={0}
                max={24}
                step={0.25}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAnimating(!isAnimating)}
              className="flex-1 border-white/10"
              data-testid="animate-time-btn"
            >
              {isAnimating ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              {isAnimating ? t('view3d.pause') : t('view3d.animateDay')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewAngle(Math.round(currentSunPos.azimuth))}
              className="border-white/10"
              data-testid="look-at-sun-btn"
            >
              <Sun className="w-4 h-4 mr-1" />
              {t('view3d.faceSun')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setViewAngle(0);
                setElevation(90);
                setFocalLength(24);
                setTimeOfDay(12);
              }}
              className="border-white/10"
              data-testid="reset-view-btn"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          {/* Quick Time Presets */}
          <div className="flex flex-wrap gap-1">
            {[
              { label: t('sun.sunrise'), time: 6 },
              { label: t('view3d.goldenAM'), time: 7 },
              { label: t('view3d.noon'), time: 12 },
              { label: t('view3d.goldenPM'), time: 18 },
              { label: t('sun.sunset'), time: 19 },
              { label: t('sun.blueHour'), time: 20 },
              { label: t('view3d.night'), time: 23 },
            ].map(preset => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                onClick={() => setTimeOfDay(preset.time)}
                className="text-xs h-6 px-2"
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sun Position Info */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs text-slate-500">{t('view3d.sunAzimuth')}</p>
              <p className="font-mono text-base text-amber-500">{currentSunPos.azimuth.toFixed(1)}°</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">{t('view3d.sunAltitude')}</p>
              <p className={`font-mono text-base ${currentSunPos.altitude > 0 ? 'text-amber-500' : 'text-blue-500'}`}>
                {currentSunPos.altitude.toFixed(1)}°
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">{t('view3d.cameraFov')}</p>
              <p className="font-mono text-base text-white">{(2 * Math.atan(36 / (2 * focalLength)) * 180 / Math.PI).toFixed(0)}°</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default View3DPlanner;
